import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import { useContent } from "../context/ContentContext";
import { useReveal } from "../hooks/useReveal";
import Seo from "../components/Seo";

const BlogPost = () => {
  const { id } = useParams();
  const { blogPosts } = useContent();
  const r = useReveal();

  const post = blogPosts.find((p) => p.id === id);
  if (!post) return <Navigate to="/blog" replace />;

  const paragraphs = (post.body || post.excerpt || "").split(/\n\s*\n|\n/).map((s) => s.trim()).filter(Boolean);
  const related = blogPosts.filter((p) => p.id !== post.id).slice(0, 3);

  return (
    <main className="pt-[76px]" data-testid="blog-post">
      <Seo
        title={post.title}
        description={post.excerpt}
        path={`/blog/${post.id}`}
        image={post.image}
      />
      {/* Hero */}
      <section className="relative h-[52vh] min-h-[380px] w-full overflow-hidden flex items-end">
        <img src={post.image} alt={post.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/25" />
        <div className="relative max-w-[860px] mx-auto px-5 md:px-8 w-full pb-12">
          <div className="flex items-center gap-4 mb-4 fade-in">
            <span className="font-body uppercase tracking-[0.16em] text-[11px] text-[#e6d3c6]">{post.category}</span>
            <span className="overline !text-white/70">{post.date}</span>
          </div>
          <h1 className="font-display font-light text-white text-[34px] md:text-[52px] leading-[1.08] fade-in">{post.title}</h1>
        </div>
      </section>

      {/* Body */}
      <article ref={r.ref} className={`max-w-[720px] mx-auto px-5 md:px-8 py-16 md:py-24 ${r.className}`}>
        <Link to="/blog" className="inline-flex items-center gap-2 text-[#6b665d] font-body uppercase tracking-[0.16em] text-[11px] hover:text-[#94664f] transition-colors mb-10">
          <ArrowLeft size={14} /> All articles
        </Link>
        <p className="font-display italic text-[22px] md:text-[26px] leading-[1.5] text-[#3a3833] mb-10 pb-10 border-b border-[#eae5db]">
          {post.excerpt}
        </p>
        {paragraphs.map((p, i) => (
          <p key={i} className="font-body text-[16px] md:text-[17px] leading-[2] text-[#4a453d] mb-7">{p}</p>
        ))}

        <div className="mt-14 pt-10 border-t border-[#eae5db]">
          <p className="font-display text-[24px] text-[#23211e] mb-4">Considering a project of your own?</p>
          <p className="font-body text-[15px] leading-[1.9] text-[#5b564d] mb-6">
            We would be delighted to discuss your requirements and share how we approach work of this kind.
          </p>
          <Link to="/contact" className="btn-primary !bg-[#94664f] hover:!bg-[#23211e]">
            Get in touch <ArrowRight size={15} />
          </Link>
        </div>
      </article>

      {/* Related */}
      {related.length > 0 && (
        <section className="bg-[#f6f2ec] py-20 md:py-24">
          <div className="max-w-[1240px] mx-auto px-5 md:px-8">
            <p className="eyebrow mb-10">More from the journal</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-12">
              {related.map((p) => (
                <Link to={`/blog/${p.id}`} key={p.id} data-testid="related-card" className="group">
                  <div className="overflow-hidden h-[220px] mb-5">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]" />
                  </div>
                  <div className="flex items-center gap-4 mb-3">
                    <span className="font-body uppercase tracking-[0.16em] text-[11px] text-[#94664f]">{p.category}</span>
                    <span className="overline">{p.date}</span>
                  </div>
                  <h3 className="font-display text-[23px] text-[#23211e] leading-[1.25] mb-2 group-hover:text-[#94664f] transition-colors">{p.title}</h3>
                  <span className="inline-flex items-center gap-1.5 text-[#94664f] font-body uppercase tracking-[0.14em] text-[10.5px] opacity-0 group-hover:opacity-100 transition-opacity">
                    Read article <ArrowUpRight size={13} />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
};

export default BlogPost;
